import OSS from 'ali-oss'

import api from '../utils/axiosConfig'
import { PaginatedDetectionResponse } from '../types/device-mosquito'
import { HistoryListItem, HistoryRecord } from '../types/history'

const MANAGED_OSS_PREFIXES = [
    'mosquito/uploads/',
    'mosquito/annotated/',
    'devices/installation-images/',
    'model-validation/',
]
const STS_BATCH_LIMIT = 10
const SIGNED_URL_CACHE_BUFFER_MS = 30_000
const MAX_SIGNED_URL_TTL_SECONDS = 600

type NullablePath = string | null | undefined

interface OssReadStsResponse {
    access_key_id: string
    access_key_secret: string
    security_token: string
    expiration: string
    bucket: string
    region: string
    endpoint: string
    object_keys: string[]
}

interface CachedSignedUrl {
    url: string
    expiresAt: number
}

const signedUrlCache = new Map<string, CachedSignedUrl>()
const SHARED_READ_STS_PATH = '/oss/sts/read'
const DEVICE_DISTRIBUTION_READ_STS_PATH = '/oss/sts/device-distribution/read'
const SHARED_SIGNED_URL_CACHE_SCOPE = 'shared'
const DEVICE_DISTRIBUTION_SIGNED_URL_CACHE_SCOPE = 'device-distribution'
const SIGNED_URL_CACHE_SCOPES = [
    SHARED_SIGNED_URL_CACHE_SCOPE,
    DEVICE_DISTRIBUTION_SIGNED_URL_CACHE_SCOPE,
]

const isDirectlyRenderablePath = (value: string): boolean =>
    value.startsWith('http://') ||
    value.startsWith('https://') ||
    value.startsWith('data:')

export const normalizeManagedObjectKey = (
    value: NullablePath
): string | null => {
    if (!value) {
        return null
    }

    const normalized = value.trim().replace(/^\/+/, '')
    if (!normalized || isDirectlyRenderablePath(normalized)) {
        return null
    }

    return MANAGED_OSS_PREFIXES.some((prefix) => normalized.startsWith(prefix))
        ? normalized
        : null
}

const buildSignedUrlCacheKey = (scope: string, objectKey: string): string =>
    `${scope}:${objectKey}`

const getCachedUrl = (scope: string, objectKey: string): string | null => {
    const cached = signedUrlCache.get(buildSignedUrlCacheKey(scope, objectKey))
    if (!cached) {
        return null
    }

    if (cached.expiresAt <= Date.now() + SIGNED_URL_CACHE_BUFFER_MS) {
        signedUrlCache.delete(buildSignedUrlCacheKey(scope, objectKey))
        return null
    }

    return cached.url
}

export const invalidateManagedObjectUrlCache = (paths: NullablePath[]) => {
    paths.forEach((path) => {
        const objectKey = normalizeManagedObjectKey(path)
        if (!objectKey) {
            return
        }

        SIGNED_URL_CACHE_SCOPES.forEach((scope) => {
            signedUrlCache.delete(buildSignedUrlCacheKey(scope, objectKey))
        })
    })
}

const getSignatureTtlSeconds = (expiration: string): number => {
    const remainingSeconds =
        Math.floor((new Date(expiration).getTime() - Date.now()) / 1000) - 60

    return Math.max(
        30,
        Math.min(MAX_SIGNED_URL_TTL_SECONDS, remainingSeconds)
    )
}

const chunkObjectKeys = (objectKeys: string[]): string[][] => {
    const batches: string[][] = []

    for (let index = 0; index < objectKeys.length; index += STS_BATCH_LIMIT) {
        batches.push(objectKeys.slice(index, index + STS_BATCH_LIMIT))
    }

    return batches
}

const createOssClient = (credentials: OssReadStsResponse) =>
    new OSS({
        accessKeyId: credentials.access_key_id,
        accessKeySecret: credentials.access_key_secret,
        stsToken: credentials.security_token,
        bucket: credentials.bucket,
        region: credentials.region,
        endpoint: credentials.endpoint,
        secure: credentials.endpoint.startsWith('https://'),
        authorizationV4: true,
    })

const replaceObjectKeyWithSignedUrl = (
    value: NullablePath,
    urlMap: Map<string, string>
): NullablePath => {
    const objectKey = normalizeManagedObjectKey(value)
    if (!objectKey) {
        return value
    }

    return urlMap.get(objectKey) || value
}

export const resolveManagedObjectUrls = async (
    paths: NullablePath[],
    stsPath = SHARED_READ_STS_PATH,
    cacheScope = SHARED_SIGNED_URL_CACHE_SCOPE
): Promise<Map<string, string>> => {
    const objectKeys = [...new Set(paths.map(normalizeManagedObjectKey).filter(Boolean))] as string[]
    const resolvedUrls = new Map<string, string>()
    const uncachedKeys: string[] = []

    objectKeys.forEach((objectKey) => {
        const cachedUrl = getCachedUrl(cacheScope, objectKey)
        if (cachedUrl) {
            resolvedUrls.set(objectKey, cachedUrl)
            return
        }

        uncachedKeys.push(objectKey)
    })

    for (const batch of chunkObjectKeys(uncachedKeys)) {
        const { data } = await api.post<OssReadStsResponse>(stsPath, {
            object_keys: batch,
        })

        const client = createOssClient(data)
        const signedUrlTtlSeconds = getSignatureTtlSeconds(data.expiration)
        const batchExpiresAt = Math.max(
            Date.now() + SIGNED_URL_CACHE_BUFFER_MS,
            Math.min(
                new Date(data.expiration).getTime(),
                Date.now() + signedUrlTtlSeconds * 1000
            ) - SIGNED_URL_CACHE_BUFFER_MS
        )

        const signedEntries = await Promise.all(
            batch.map(async (objectKey) => {
                const signedUrl = await client.signatureUrlV4(
                    'GET',
                    signedUrlTtlSeconds,
                    undefined,
                    objectKey
                )
                return [objectKey, signedUrl] as const
            })
        )

        signedEntries.forEach(([objectKey, signedUrl]) => {
            signedUrlCache.set(
                buildSignedUrlCacheKey(cacheScope, objectKey),
                {
                    url: signedUrl,
                    expiresAt: batchExpiresAt,
                }
            )
            resolvedUrls.set(objectKey, signedUrl)
        })
    }

    return resolvedUrls
}

export const hydrateHistoryRecordsWithSignedUrls = async (
    records: HistoryRecord[]
): Promise<HistoryRecord[]> => {
    const urlMap = await resolveManagedObjectUrls(
        records.flatMap((record) => [
            record.thumbnail,
            record.result?.source_image,
            record.result?.annotated_image,
            record.manual_recognition?.annotated_image,
            record.effective_annotated_image,
        ])
    )

    return records.map((record) => ({
        ...record,
        thumbnail: replaceObjectKeyWithSignedUrl(record.thumbnail, urlMap),
        effective_annotated_image: replaceObjectKeyWithSignedUrl(
            record.effective_annotated_image,
            urlMap
        ),
        manual_recognition: record.manual_recognition
            ? {
                  ...record.manual_recognition,
                  annotated_image: replaceObjectKeyWithSignedUrl(
                      record.manual_recognition.annotated_image,
                      urlMap
                  ),
              }
            : record.manual_recognition,
        result: {
            ...record.result,
            source_image: replaceObjectKeyWithSignedUrl(
                record.result?.source_image,
                urlMap
            ),
            annotated_image: replaceObjectKeyWithSignedUrl(
                record.result?.annotated_image,
                urlMap
            ),
        },
    }))
}

export const hydrateHistoryListItemsWithSignedUrls = async (
    items: HistoryListItem[]
): Promise<HistoryListItem[]> => {
    const urlMap = await resolveManagedObjectUrls(
        items.flatMap((item) => [item.thumbnail, item.source_image])
    )

    return items.map((item) => ({
        ...item,
        thumbnail: replaceObjectKeyWithSignedUrl(item.thumbnail, urlMap),
        source_image: replaceObjectKeyWithSignedUrl(item.source_image, urlMap),
    }))
}

export const hydrateHistoryRecordWithSignedUrls = async (
    record: HistoryRecord
): Promise<HistoryRecord> => {
    const [hydratedRecord] = await hydrateHistoryRecordsWithSignedUrls([record])
    return hydratedRecord
}

export const hydratePaginatedDetectionsWithSignedUrls = async (
    response: PaginatedDetectionResponse
): Promise<PaginatedDetectionResponse> => {
    const urlMap = await resolveManagedObjectUrls(
        response.items.flatMap((item) => [
            item.image_path,
            item.annotated_path,
            item.effective_annotated_path,
        ]),
        DEVICE_DISTRIBUTION_READ_STS_PATH,
        DEVICE_DISTRIBUTION_SIGNED_URL_CACHE_SCOPE
    )

    return {
        ...response,
        items: response.items.map((item) => ({
            ...item,
            image_path: replaceObjectKeyWithSignedUrl(item.image_path, urlMap),
            annotated_path: replaceObjectKeyWithSignedUrl(
                item.annotated_path,
                urlMap
            ),
            effective_annotated_path: replaceObjectKeyWithSignedUrl(
                item.effective_annotated_path,
                urlMap
            ),
        })),
    }
}
